import type { AxiosError, AxiosResponse, AxiosRequestConfig } from "axios"
import axios from "axios"

import type { ValueOf } from "../../types"
import { getFileName } from "../../utils/AxiosResponseUtils"
import type { Reviver } from "./createReviver"
import ERROR_CODE from "./errorCode"
import { replacePathParamsOfURL } from "./replacePathParams"
import { traverseJson } from "./traverseJson"

const isJsonResponse = (response: AxiosResponse): boolean => {
  return response.headers["content-type"]?.includes("application/json") ?? false
}

const isTomcatDown = (error: unknown) =>
  axios.isAxiosError(error) &&
  error.response?.status === 503 &&
  (error.response.headers["content-type"] as string).includes("text/html")

const isNetworkError = (error: unknown): error is AxiosError =>
  axios.isAxiosError(error) && error.response === undefined && !isTimeoutError(error)
const isTimeoutError = (error: unknown) => axios.isAxiosError(error) && error.code === "ECONNABORTED"

type SuccessResult<S> = { isSuccess: true; isError: false; data: S }
type ErrorResult<E> = { isSuccess: false; isError: true; data: E }
type Result<S, E> = SuccessResult<S> | ErrorResult<E>
type Payload = { [key: string]: unknown } | FormData
type RequestOptions<S> = {
  queryParams?: { [key: string]: string | number }
  pathParams?: { [key: string]: string | number }
  /**
   * @deprecated reviverを使用してください。
   */
  transform?: (data: any) => S
  reviver?: Reviver
  responseType?: "json" | "file"
  timeout?: number
}

type ErrorKeys<T extends Record<string, unknown>> = ValueOf<{
  [Key in keyof T]-?: Key extends string
    ? T[Key] extends any[]
      ? Key | `${Key}[${number}]`
      : T[Key] extends Record<string, unknown>
      ? Key | `${Key}.${ErrorKeys<T[Key]>}`
      : Key
    : never
}>

export type SpringApi<
  TArg = unknown,
  TSuccess = unknown,
  TError extends Record<string, unknown> = Record<string, unknown>
> = (arg: TArg) => Promise<Result<TSuccess, SpringError<TError>>>

type SpringSingleError = {
  message: string
  code: string | null
  objectName?: undefined
  details?: undefined
}
export type SpringError<T extends Record<string, unknown> | undefined = undefined> = T extends {
  [key: string]: unknown
}
  ?
      | SpringSingleError
      | {
          message: string
          code?: undefined
          objectName: string
          details?: {
            type: string
            field: ErrorKeys<T>
            message: string
          }[]
        }
  : SpringSingleError

type HttpClientOptions = {
  origin?: string
  onServerMaintenance?: () => void
  onUnauthorizedError?: () => void
  onUnknownError?: (e: unknown) => void
}

export const createHttpClient = ({
  origin,
  onServerMaintenance,
  onUnauthorizedError,
  onUnknownError,
}: HttpClientOptions = {}) => {
  const httpRequest = <TSuccess, TError>(
    method: "GET" | "POST" | "PUT" | "DELETE",
    urlString: string,
    data?: Payload,
    {
      pathParams = {},
      queryParams,
      transform,
      reviver,
      responseType = "json",
      timeout = 30 * 1000,
    }: RequestOptions<TSuccess> = {}
  ): Promise<Result<TSuccess, TError>> => {
    const url = new URL(urlString, origin ?? window.location.origin)
    const config: AxiosRequestConfig = {
      method,
      url: replacePathParamsOfURL(url, pathParams).toString(),
      params: queryParams,
      data,
      timeout,
      responseType: "blob",
    }

    return axios
      .request<TSuccess>(config)
      .then(async (res) => {
        const blob: Blob = res.data as any

        switch (responseType) {
          case "json": {
            // NOTE: axiosの処理方法に合わせJSONとして不正な文字列の場合例外を投げず、元々の文字列をそのまま返すようにしています。
            let json: any = await blob.text()
            try {
              json = JSON.parse(json)
            } catch {}

            return {
              isSuccess: true,
              isError: false,
              data: reviver ? traverseJson(json, reviver) : transform?.(json) ?? json,
            } as const
          }

          case "file": {
            const filename = getFileName(res)
            if (filename === undefined) {
              throw new Error("filenameが取得できません。")
            }

            return {
              isSuccess: true,
              isError: false,
              data: new File([blob], filename, { type: res.headers["content-type"] }),
            } as const
          }

          default:
            throw new RangeError(`responseTypeの値が不正です。value: ${responseType}`)
        }
      })
      .catch(async (e: unknown) => {
        if (axios.isAxiosError(e) && e.response && isJsonResponse(e.response)) {
          const data = JSON.parse(await (e.response.data as Blob).text())

          if (data?.code === ERROR_CODE.UNDER_MAINTENANCE) {
            onServerMaintenance?.()
          }

          switch (e.response.status) {
            case 401:
              onUnauthorizedError?.()
            case 400:
            case 413:
            case 500:
            case 503:
              return { isSuccess: false, isError: true, data }
          }
        }

        if (!isNetworkError(e) && !isTimeoutError(e) && !isTomcatDown(e)) {
          onUnknownError?.(e)
        }

        let message = "システムエラーが発生しました。"
        if (isNetworkError(e)) message = "ネットワークエラーが発生しました。"
        if (isTimeoutError(e)) message = "接続がタイムアウトしました。"

        return {
          isSuccess: false,
          isError: true,
          data: {
            message: `${message}しばらくしてからもう一度お試しください。`,
            code: null,
          },
        }
      })
  }

  const httpClient = {
    get<TSuccess = unknown, TError = SpringError>(url: string, options?: RequestOptions<TSuccess>) {
      return httpRequest<TSuccess, TError>("GET", url, undefined, options)
    },
    post<TSuccess = unknown, TError = SpringError>(url: string, data: Payload, options?: RequestOptions<TSuccess>) {
      return httpRequest<TSuccess, TError>("POST", url, data, options)
    },
    put<TSuccess = unknown, TError = SpringError>(url: string, data: Payload, options?: RequestOptions<TSuccess>) {
      return httpRequest<TSuccess, TError>("PUT", url, data, options)
    },
    delete<TSuccess = unknown, TError = SpringError>(url: string, data?: Payload, options?: RequestOptions<TSuccess>) {
      return httpRequest<TSuccess, TError>("DELETE", url, data, options)
    },
  } as const

  return httpClient
}
